import React, { useState } from 'react';
import lodashCompact from 'lodash/fp/compact';
import lodashUniq from 'lodash/fp/uniq';

import { DATA_TABLE_SOURCE_ADDRESS_RESOLUTION } from 'common/dataTableConstants';
import {
  ADDRESS_RESOLUTION_STATUS_INVALID_MATCH,
  ADDRESS_RESOLUTION_STATUS_NO_MATCH,
  ADDRESS_RESOLUTION_RECOVERABLE_SOURCES,
  ADDRESS_RESOLUTION_STATUS_TO_LABEL_MAPPING,
  PARTNER_ADDRESS_STATUS_PENDING_VERIFICATION,
  PARTNER_ADDRESS_SOURCE_PARTNER_MANUAL_ADDITION,
} from 'common/utility/constants';

import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrText from 'client/components/SrText';
import UPAddressResolutionDetailsModal from 'client/components/UPAddressResolutionDetailsModal/UPAddressResolutionDetailsModal';
import UPAddressVerificationDetailsModal from 'client/components/UPAddressVerificationDetailsModal/UPAddressVerificationDetailsModal';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import SrButton, {
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from '../SrButton/SrButton';

import BulkActionBar from './BulkActionBar';
import BulkIgnoreConfirmModal from './BulkIgnoreConfirmModal';
import BulkResultBanner, {
  BANNER_VARIANT_ERROR,
  BANNER_VARIANT_SUCCESS,
  BANNER_VARIANT_WARNING,
} from './BulkResultBanner';
import BulkSelectCheckbox, {
  BULK_SELECT_STATE_CHECKED,
  BULK_SELECT_STATE_INDETERMINATE,
  BULK_SELECT_STATE_UNCHECKED,
} from './BulkSelectCheckbox';
import useBulkIgnoreController, {
  TASK_TYPE_ADDRESS_RESOLUTION,
  TASK_TYPE_ADDRESS_VERIFICATION,
} from './useBulkIgnoreController';

const NO_MATCH_COUNT = 20;

const formatDate = (date) => {
  return momentWithAustinTimezone(date).format('MMM D, YYYY');
};

const itemKey = (item) => `${item.taskType}:${item.id}`;

const headerCheckboxState = (visibleItems, selection) => {
  if (!visibleItems.length) return BULK_SELECT_STATE_UNCHECKED;
  const selectedVisible = visibleItems.filter((it) => selection.has(itemKey(it))).length;
  if (selectedVisible === 0) return BULK_SELECT_STATE_UNCHECKED;
  if (selectedVisible === visibleItems.length) return BULK_SELECT_STATE_CHECKED;
  return BULK_SELECT_STATE_INDETERMINATE;
};

const initialDataTableFilters = {
  status: [
    ADDRESS_RESOLUTION_STATUS_NO_MATCH,
    ADDRESS_RESOLUTION_STATUS_INVALID_MATCH,
    PARTNER_ADDRESS_STATUS_PENDING_VERIFICATION,
  ],
  source: [
    PARTNER_ADDRESS_SOURCE_PARTNER_MANUAL_ADDITION,
    ...ADDRESS_RESOLUTION_RECOVERABLE_SOURCES,
  ],
};

export function getDataTableSettingsFromProps() {
  return {
    dataSource: DATA_TABLE_SOURCE_ADDRESS_RESOLUTION,
    rootViewId: DATA_TABLE_SOURCE_ADDRESS_RESOLUTION,
    initialViewSettings: {
      sort: {
        createdAt: 'desc',
      },
      filter: initialDataTableFilters,
    },
    count: NO_MATCH_COUNT,
    itemsPropName: 'items',
    resetOnUnmount: true,
  };
}

const buildHeaders = ({ headerSelectState, onToggleAllVisible }) => [
  {
    content: (
      <BulkSelectCheckbox
        state={headerSelectState}
        onClick={onToggleAllVisible}
        ariaLabel="Select all visible rows"
      />
    ),
    sortPosition: 'right',
    style: { width: '32px' },
  },
  {
    content: 'Created Date',
    dataTableFieldId: 'createdAt',
    sortPosition: 'right',
    style: { width: '108px' },
  },
  {
    content: 'Partner',
    dataTableFieldId: 'partnerName',
    sortPosition: 'right',
    style: { width: '150px' },
  },
  {
    content: 'Search Term',
    sortPosition: 'right',
    style: { width: '300px' },
  },
  {
    content: 'Status',
    sortPosition: 'right',
    style: { width: '140px' },
  },
  {
    content: 'Source',
    sortPosition: 'right',
    style: { width: '140px' },
  },
  {
    content: '',
    sortPosition: 'right',
    style: { width: '120px' },
  },
];

const buildRows = ({ items, selection, onToggleRow, onResolveClick }) => {
  return items.map((item) => {
    const key = itemKey(item);
    const isSelected = selection.has(key);
    return {
      cells: [
        {
          content: (
            <BulkSelectCheckbox
              state={isSelected ? BULK_SELECT_STATE_CHECKED : BULK_SELECT_STATE_UNCHECKED}
              onClick={() => onToggleRow(item)}
              ariaLabel={`Select address ${item.searchTerm ?? item.id}`}
            />
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="700">
              {formatDate(item.createdAt)}
            </SrText>
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="700">
              {item.partnerName}
            </SrText>
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="600">
              {item.searchTerm}
            </SrText>
          ),
          style: {
            maxWidth: '340px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        {
          content: (
            <SrText font="greycliff" weight="600">
              {ADDRESS_RESOLUTION_STATUS_TO_LABEL_MAPPING[item.status] ??
                camelCaseToTitleCase(item.status)}
            </SrText>
          ),
        },
        {
          content: (
            <SrText font="greycliff" weight="600">
              {camelCaseToTitleCase(item.source)}
            </SrText>
          ),
        },
        {
          content: (
            <SrButton
              heightType={SR_BUTTON_HEIGHT_SHORT}
              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
              disabled={isSelected}
              onClick={() => onResolveClick(item)}
            >
              Process
            </SrButton>
          ),
        },
      ],
    };
  });
};

const buildLoadMoreRow = ({ dataTableIsFullyLoaded, dataTableApiState, loadNextPage }) => {
  if (dataTableIsFullyLoaded) {
    return null;
  }

  return {
    onClick: () => (dataTableApiState.inProgress ? null : loadNextPage()),
    cells: [
      {
        content: (
          <div style={{ height: '100%', textAlign: 'center', backgroundColor: '#F5F9FF' }}>
            <SrText lineHeight="50px" font="greycliff" weight="800" size="16" color="blue300">
              View more items
            </SrText>
          </div>
        ),
        colSpan: 7,
        noPadding: true,
      },
    ],
  };
};

/**
 * Inner component that owns hooks. Wrapped by `UPAddressResolutionNoMatchDataTable` whose
 * sole job is to provide the `SrDataTableConnected` render-prop. Splitting them keeps
 * `useBulkIgnoreController` (and other hooks) at the top level of a component instead of
 * inside the render-prop callback (rules-of-hooks).
 */
const TableContent = ({
  items,
  dataTableApiState,
  viewSettings,
  loadNextPage,
  dataTableIsFullyLoaded,
  reloadItems,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selection, setSelection] = useState(new Map());
  const [banner, setBanner] = useState(null);

  const onResolveClick = (item) => {
    setSelectedItem(item);
  };

  const onCloseModal = () => {
    setSelectedItem(null);
  };

  const toggleRow = (item) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const key = itemKey(item);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, { id: item.id, taskType: item.taskType });
      }
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (!items || items.length === 0) return;
    setSelection((prev) => {
      const allSelected = items.every((it) => prev.has(itemKey(it)));
      const next = new Map(prev);
      if (allSelected) {
        items.forEach((it) => next.delete(itemKey(it)));
      } else {
        items.forEach((it) => next.set(itemKey(it), { id: it.id, taskType: it.taskType }));
      }
      return next;
    });
  };

  const clearSelection = () => setSelection(new Map());

  const handleBulkComplete = (result) => {
    const okCount =
      result.successful.resolutionAttemptIds.length + result.successful.listingIds.length;
    const failedCount = result.failed.length;
    const reasons = lodashUniq(result.failed.map((f) => f.reason));

    if (failedCount === 0) {
      setBanner({ variant: BANNER_VARIANT_SUCCESS, successCount: okCount, failedCount: 0 });
      clearSelection();
    } else if (okCount === 0) {
      setBanner({
        variant: BANNER_VARIANT_ERROR,
        successCount: 0,
        failedCount,
        reasons,
      });
      // selection stays — every selected item failed
    } else {
      setBanner({
        variant: BANNER_VARIANT_WARNING,
        successCount: okCount,
        failedCount,
        reasons,
      });
      const failedKeys = new Set(result.failed.map((f) => `${f.taskType}:${f.id}`));
      setSelection((prev) => {
        const next = new Map();
        for (const [key, value] of prev) {
          if (failedKeys.has(key)) next.set(key, value);
        }
        return next;
      });
    }

    if (typeof reloadItems === 'function') {
      // Mirror the single-ignore reload pattern: dispatch `removeEntityId` per
      // successful id. This removes each row from the store immediately and
      // backfills one new row per removal — keeping the visible page count
      // stable. Passing `count` alone is not enough because the data table
      // helper appends to the existing page (skip=loadedCount).
      const successfulIds = [
        ...result.successful.resolutionAttemptIds,
        ...result.successful.listingIds,
      ];
      successfulIds.forEach((id) => {
        reloadItems({ removeEntityId: id });
      });
    }
  };

  const bulkController = useBulkIgnoreController({
    selection,
    onComplete: handleBulkComplete,
  });

  if (dataTableApiState.inProgress && items.length === 0) {
    return (
      <div>
        <div className="loading-ellipses clearfix" />
      </div>
    );
  }

  const headers = buildHeaders({
    headerSelectState: headerCheckboxState(items, selection),
    onToggleAllVisible: toggleAllVisible,
  });
  const rows = buildRows({
    items,
    selection,
    onToggleRow: toggleRow,
    onResolveClick,
  });
  const loadMoreRow = buildLoadMoreRow({
    dataTableIsFullyLoaded,
    dataTableApiState,
    loadNextPage,
  });

  return (
    <SrBox marginTop="20px" paddingBottom="56px">
      {banner && (
        <BulkResultBanner
          variant={banner.variant}
          successCount={banner.successCount}
          failedCount={banner.failedCount}
          reasons={banner.reasons}
          onClose={() => setBanner(null)}
        />
      )}
      {bulkController.state.selectedCount > 0 && (
        <BulkActionBar
          selectedCount={bulkController.state.selectedCount}
          onIgnore={bulkController.handlers.openConfirm}
          onClear={clearSelection}
        />
      )}
      <div className="up-address-resolution-no-match-table">
        <SrDataTableUI
          sticky
          theme={SR_TABLE_THEME_PARTNER_CLEAR}
          dataTableViewSettings={viewSettings}
          headers={headers}
          maxWidth="100%"
          rows={lodashCompact([...rows, loadMoreRow])}
        />
        {dataTableApiState.inProgress && (
          <div>
            <div className="loading-ellipses clearfix" />
          </div>
        )}
      </div>
      {items.length === 0 && dataTableIsFullyLoaded && (
        <SrBox
          backgroundColor="#F5F9FF"
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          padding="20px"
          opacity="0.8"
          cursor="default"
        >
          <SrText font="greycliff" weight="500" fontSize="16px">
            No address resolution attempts found.
          </SrText>
        </SrBox>
      )}
      {selectedItem &&
        (selectedItem?.taskType === TASK_TYPE_ADDRESS_VERIFICATION ? (
          <UPAddressVerificationDetailsModal
            item={selectedItem}
            onClose={onCloseModal}
            reloadItems={reloadItems}
          />
        ) : (
          <UPAddressResolutionDetailsModal
            item={selectedItem}
            onClose={onCloseModal}
            reloadItems={reloadItems}
          />
        ))}
      {bulkController.state.showConfirm && (
        <BulkIgnoreConfirmModal
          count={bulkController.state.selectedCount}
          isSubmitting={bulkController.state.isSubmitting}
          errorMessage={bulkController.state.errorMessage}
          onConfirm={bulkController.handlers.submit}
          onClose={bulkController.handlers.closeConfirm}
        />
      )}
    </SrBox>
  );
};

const UPAddressResolutionNoMatchDataTable = () => (
  <SrDataTableConnected {...getDataTableSettingsFromProps()}>
    {(renderProps) => <TableContent {...renderProps} />}
  </SrDataTableConnected>
);

UPAddressResolutionNoMatchDataTable.displayName = 'UPAddressResolutionNoMatchDataTable';

export { TASK_TYPE_ADDRESS_RESOLUTION, TASK_TYPE_ADDRESS_VERIFICATION };

export default UPAddressResolutionNoMatchDataTable;
